import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listWorkflows,
  getWorkflow,
  getWorkflowGraph,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
  duplicateWorkflow,
  updateWorkflowStatus,
  saveWorkflowGraph,
  listTemplates,
  createFromTemplate,
  generateWorkflow,
  testWorkflow,
  executeWorkflow,
  listWorkflowRuns,
  getWorkflowRun,
  getRunSteps,
  getNotifications,
  getTasks,
  type ListWorkflowsParams,
  type CreateWorkflowBody,
  type UpdateWorkflowBody,
  type SaveWorkflowGraphBody,
  type ListWorkflowRunsParams,
  type ListParams,
} from '@/api/workflows';

const WORKFLOWS_QUERY_KEY = 'workflows';
const WORKFLOW_QUERY_KEY = 'workflow';
const WORKFLOW_GRAPH_QUERY_KEY = 'workflow-graph';
const TEMPLATES_QUERY_KEY = 'workflow-templates';
const WORKFLOW_RUNS_QUERY_KEY = 'workflow-runs';
const WORKFLOW_RUN_QUERY_KEY = 'workflow-run';
const NOTIFICATIONS_QUERY_KEY = 'workflow-notifications';
const TASKS_QUERY_KEY = 'workflow-tasks';

export function useWorkflows(params?: ListWorkflowsParams) {
  return useQuery({
    queryKey: [WORKFLOWS_QUERY_KEY, params],
    queryFn: () => listWorkflows(params),
  });
}

export function useWorkflow(id: string | null) {
  return useQuery({
    queryKey: [WORKFLOW_QUERY_KEY, id],
    queryFn: () => getWorkflow(id!),
    enabled: !!id,
  });
}

export function useWorkflowGraph(id: string | null) {
  return useQuery({
    queryKey: [WORKFLOW_GRAPH_QUERY_KEY, id],
    queryFn: () => getWorkflowGraph(id!),
    enabled: !!id,
  });
}

export function useWorkflowTemplates() {
  return useQuery({
    queryKey: [TEMPLATES_QUERY_KEY],
    queryFn: listTemplates,
  });
}

export function useWorkflowRuns(workflowId: string | null, params?: ListWorkflowRunsParams) {
  return useQuery({
    queryKey: [WORKFLOW_RUNS_QUERY_KEY, workflowId, params],
    queryFn: () => listWorkflowRuns(workflowId!, params),
    enabled: !!workflowId,
  });
}

export function useWorkflowRun(runId: string | null) {
  return useQuery({
    queryKey: [WORKFLOW_RUN_QUERY_KEY, runId],
    queryFn: () => getWorkflowRun(runId!),
    enabled: !!runId,
  });
}

export function useRunSteps(runId: string | null) {
  return useQuery({
    queryKey: [WORKFLOW_RUN_QUERY_KEY, runId, 'steps'],
    queryFn: () => getRunSteps(runId!),
    enabled: !!runId,
  });
}

export function useWorkflowNotifications(params?: ListParams) {
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, params],
    queryFn: () => getNotifications(params),
  });
}

export function useWorkflowTasks(params?: ListParams) {
  return useQuery({
    queryKey: [TASKS_QUERY_KEY, params],
    queryFn: () => getTasks(params),
  });
}

export function useCreateWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWorkflowBody) => createWorkflow(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_QUERY_KEY] });
    },
  });
}

export function useUpdateWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkflowBody }) =>
      updateWorkflow(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [WORKFLOW_QUERY_KEY, id] });
    },
  });
}

export function useDeleteWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWorkflow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_QUERY_KEY] });
    },
  });
}

export function useDuplicateWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => duplicateWorkflow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_QUERY_KEY] });
    },
  });
}

export function useUpdateWorkflowStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateWorkflowStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [WORKFLOW_QUERY_KEY, id] });
    },
  });
}

export function useSaveWorkflowGraph() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SaveWorkflowGraphBody }) =>
      saveWorkflowGraph(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOW_QUERY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [WORKFLOW_GRAPH_QUERY_KEY, id] });
    },
  });
}

export function useCreateFromTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => createFromTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_QUERY_KEY] });
    },
  });
}

export function useGenerateWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (description: string) => generateWorkflow(description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_QUERY_KEY] });
    },
  });
}

export function useTestWorkflow(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (triggerData?: Record<string, unknown>) => testWorkflow(workflowId!, triggerData),
    onSuccess: () => {
      if (workflowId) {
        queryClient.invalidateQueries({
          queryKey: [WORKFLOW_RUNS_QUERY_KEY, workflowId],
        });
      }
    },
  });
}

export function useExecuteWorkflow(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (triggerData?: Record<string, unknown>) =>
      executeWorkflow(workflowId!, triggerData),
    onSuccess: () => {
      if (workflowId) {
        queryClient.invalidateQueries({
          queryKey: [WORKFLOW_RUNS_QUERY_KEY, workflowId],
        });
      }
    },
  });
}
